/**
 * Shared visual class string for the boxed selection controls (Checkbox and
 * Radio). Both render a hidden native `peer` input next to a styled sibling box
 * whose state is driven by `peer-*` variants. This constant owns every colour
 * and state class the two share on the DS V2 `selection/` token family; each
 * component adds only its own corner radius, revealed glyph, and (for Checkbox)
 * the indeterminate state.
 */
import type React from "react";

import checkIcon from "@/assets/icons/check.svg";
import minusIcon from "@/assets/icons/minus.svg";

export const selectionControlBoxClassName =
  "flex size-full cursor-pointer items-center justify-center border bg-background-default text-text-default-inverse transition-colors " +
  "border-selection-border " +
  "peer-hover:border-selection-border-hover peer-hover:bg-selection-surface-hover " +
  "peer-focus-visible:ring-1 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-selection-border-hover " +
  "peer-checked:border-selection-selected peer-checked:bg-selection-selected " +
  "peer-checked-hover:border-selection-selected-hover peer-checked-hover:bg-selection-selected-hover " +
  "peer-disabled:border-selection-disabled peer-disabled:bg-selection-disabled-surface " +
  "peer-checked-disabled:border-transparent peer-checked-disabled:bg-selection-selected-disabled " +
  "peer-checked-all-child:block";

export function SelectionControlGlyph({ variant = "check" }: { variant?: "check" | "minus" }): React.ReactNode {
  const name = variant === "check" ? checkIcon : minusIcon;

  return (
    <span className="absolute hidden shrink-0 fill-transparent">
      <svg width={16} height={16} aria-hidden="true">
        <use xlinkHref={`#${name}`} />
      </svg>
    </span>
  );
}
